/* global debounce */

const HANDLING_TIME_PROPERTY = '_Handling Time';
const HANDLING_TIME_IN_STOCK = '24 Hours';
const HANDLING_TIME_PREORDER = '5-8 Business Days';

function parseBooleanValue(value) {
  return value === true || value === 'true';
}

function parseNumberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseJsonValue(value, fallback = {}) {
  if (!value) return fallback;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

function computePreorderState({
  hasPreorderTag,
  inventoryValid,
  doralAvailable,
  contarmarketAvailable,
  requestedQuantity,
  legacyPreorder
}) {
  if (!hasPreorderTag) {
    return {
      showMessage: false,
      handlingTime: null
    };
  }

  if (inventoryValid) {
    const showMessage = requestedQuantity > doralAvailable && contarmarketAvailable > 0;

    return {
      showMessage,
      handlingTime: showMessage ? HANDLING_TIME_PREORDER : HANDLING_TIME_IN_STOCK
    };
  }

  if (legacyPreorder) {
    return {
      showMessage: true,
      handlingTime: HANDLING_TIME_PREORDER
    };
  }

  return {
    showMessage: false,
    handlingTime: null
  };
}

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    connectedCallback() {
      if (!this.form) {
        this.init();
      } else if (this.boundHandleSubmit) {
        this.form.addEventListener('submit', this.boundHandleSubmit);
        if (this.quantitySelector && this.boundSyncPreorderState) {
          this.quantitySelector.addEventListener('change', this.boundSyncPreorderState);
          this.syncQuantityChangeBinding();
        }
        document.addEventListener('on:variant:change', this.boundSetMaximumInputQuantity);
        if (this.variantChangeListener) {
          document.addEventListener('on:variant:change', this.variantChangeListener);
        }
        if (this.boundHandleCartAdd) {
          document.addEventListener('on:cart:add', this.boundHandleCartAdd);
        }
        if (this.boundHandleLineItemChange) {
          document.addEventListener('on:line-item:change', this.boundHandleLineItemChange);
        }
        this.bindVolumeTriggerRoot();
      }
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.formError = this.querySelector('.js-form-error');
        this.quantityError = this.querySelector('.js-product-form-errors');
        this.handlingTimeInput = this.form.querySelector('[data-handling-time-input]');
        this.preorderMessage = this.dataset.preorderMessageId
          ? document.getElementById(this.dataset.preorderMessageId)
          : null;
        this.volumeBlock = this.resolveVolumeBlock();
        this.ensureErrorElements();

        this.boundHandleSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.boundHandleSubmit);

        this.quantitySelector = this.querySelector('quantity-input');
        if (this.quantitySelector) {
          this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
          this._confirmedQuantity = parseNumberValue(this.dataset.cartQuantity, 0);
          this._desiredQuantity = this.dataset.showQuantitySelector === 'true'
            ? parseNumberValue(this.quantityInput?.value, this._confirmedQuantity)
            : this._confirmedQuantity;
          this.boundSyncPreorderState = this.boundSyncPreorderState
            || this.syncPreorderState.bind(this);
          this.quantitySelector.addEventListener('change', this.boundSyncPreorderState);
          this.syncQuantityChangeBinding();
        }

        this.variantChangeListener = this.variantChangeListener || this.handleVariantChange.bind(this);
        document.addEventListener('on:variant:change', this.variantChangeListener);
        this.boundHandleCartAdd = this.boundHandleCartAdd || this.handleCartAdd.bind(this);
        this.boundHandleLineItemChange = this.boundHandleLineItemChange
          || this.handleLineItemChange.bind(this);
        document.addEventListener('on:cart:add', this.boundHandleCartAdd);
        document.addEventListener('on:line-item:change', this.boundHandleLineItemChange);

        this.boundHandleVolumeTrigger = this.boundHandleVolumeTrigger
          || this.handleVolumeTrigger.bind(this);
        this.bindVolumeTriggerRoot();

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        this.boundSetMaximumInputQuantity = this.setMaximumInputQuantity.bind(this);
        document.addEventListener('on:variant:change', this.boundSetMaximumInputQuantity);
        this.syncPreorderState();
        this.syncStorefrontPricing();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.boundSetMaximumInputQuantity);
      document.removeEventListener('on:variant:change', this.variantChangeListener);
      document.removeEventListener('on:cart:add', this.boundHandleCartAdd);
      document.removeEventListener('on:line-item:change', this.boundHandleLineItemChange);
      if (this.form && this.boundHandleSubmit) {
        this.form.removeEventListener('submit', this.boundHandleSubmit);
      }
      if (this.quantitySelector && this.boundHandleChange) {
        this.quantitySelector.removeEventListener('change', this.boundHandleChange);
      }
      if (this.quantitySelector && this.boundSyncPreorderState) {
        this.quantitySelector.removeEventListener('change', this.boundSyncPreorderState);
      }
      this.unbindVolumeTriggerRoot();
    }

    static addDescribedBy(element, id) {
      if (!element || !id) return;

      const ids = new Set((element.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      ids.add(id);
      element.setAttribute('aria-describedby', Array.from(ids).join(' '));
    }

    static removeDescribedBy(element, id) {
      if (!element || !id) return;

      const ids = (element.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      const nextIds = ids.filter((currentId) => currentId !== id);

      if (nextIds.length) {
        element.setAttribute('aria-describedby', nextIds.join(' '));
      } else {
        element.removeAttribute('aria-describedby');
      }
    }

    static normalizeId(value) {
      return String(value || 'product-form')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'product-form';
    }

    ensureErrorElements() {
      const idBase = ProductForm.normalizeId(this.form?.id || this.dataset.variantId || this.id);

      if (this.formError && !this.formError.id) {
        this.formError.id = `${idBase}-error`;
      }

      if (this.quantityError && !this.quantityError.id) {
        this.quantityError.id = `${idBase}-quantity-error`;
      }
    }

    setControlError(control, errorEl) {
      if (!control || !errorEl) return;

      control.setAttribute('aria-invalid', 'true');
      ProductForm.addDescribedBy(control, errorEl.id);
    }

    clearControlError(control, errorEl) {
      if (!control || !errorEl) return;

      control.removeAttribute('aria-invalid');
      ProductForm.removeDescribedBy(control, errorEl.id);
    }

    static formatMoney(cents, withCurrency = false) {
      const Pricing = window.ContarStorefrontPricing;
      if (Pricing && typeof Pricing.formatMoney === 'function') {
        return Pricing.formatMoney(cents, withCurrency);
      }

      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        const themeSettings = window.theme && window.theme.settings ? window.theme.settings : {};
        const hasCurrencySymbol = (format) => typeof format === 'string' && /[$€£¥]/.test(format);
        const moneyFormat = themeSettings.moneyFormat || Shopify.money_format;
        const moneyWithCurrencyFormat = themeSettings.moneyWithCurrencyFormat || Shopify.money_with_currency_format;
        const fallbackMoneyFormat = hasCurrencySymbol(moneyFormat)
          ? moneyFormat
          : (hasCurrencySymbol(moneyWithCurrencyFormat) ? moneyWithCurrencyFormat : '${{amount}}');
        const format = withCurrency
          ? (hasCurrencySymbol(moneyWithCurrencyFormat) ? moneyWithCurrencyFormat : fallbackMoneyFormat)
          : fallbackMoneyFormat;
        return Shopify.formatMoney(cents, format);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    hasManagedPreorderInventory() {
      return parseBooleanValue(this.dataset.hasPreorderTag)
        && parseBooleanValue(this.dataset.preorderInventoryValid);
    }

    supportsQuantitySelectorTakeover() {
      return this.hasAttribute('data-show-quantity-selector');
    }

    getCartDrawer() {
      // History/error reconciliation can replace the complete cart-drawer
      // section. Never keep using the detached instance captured during init.
      if (!this.cartDrawer || !this.cartDrawer.isConnected) {
        this.cartDrawer = document.querySelector('cart-drawer');
      }

      return this.cartDrawer;
    }

    getCartDrawerSectionId() {
      const cartDrawer = this.getCartDrawer();
      const section = cartDrawer?.closest('.shopify-section');
      return section?.id.replace('shopify-section-', '') || '';
    }

    syncQuantityChangeBinding() {
      if (!this.quantitySelector) return;

      // Capture the quantity while the DOM event is being handled. Keeping the
      // Event itself inside a debounce is unsafe because response reconciliation
      // can change evt.target.value before the callback runs.
      this.boundHandleChange = this.boundHandleChange || this.handleChange.bind(this);
      this.debouncedQuantityCommit = this.debouncedQuantityCommit || debounce((intent) => {
        if (!intent || intent.intentVersion !== this._quantityIntentVersion) return;

        this._quantityDebouncePending = false;
        this.updateQuantity(intent.quantity, {
          intentVersion: intent.intentVersion,
          optimisticIncrementApplied: true
        });
      });
      this.quantitySelector.removeEventListener('change', this.boundHandleChange);

      if (parseBooleanValue(this.dataset.showQuantitySelector)) {
        this.quantitySelector.addEventListener('change', this.boundHandleChange);
      }
    }

    getRequestedQuantity(nextQuantity) {
      const fallbackInput = this.form.querySelector('[name="quantity"]');
      const currentInputValue = this.quantityInput || fallbackInput;
      const selectedQuantity = Number.isFinite(nextQuantity)
        ? nextQuantity
        : parseNumberValue(currentInputValue?.value, 1);

      if (this.dataset.showQuantitySelector === 'true') {
        return selectedQuantity;
      }

      return parseNumberValue(this.dataset.cartQuantity, 0) + selectedQuantity;
    }

    getPricingEvaluationQuantity(nextQuantity) {
      const Pricing = window.ContarStorefrontPricing;
      if (Pricing && typeof Pricing.getEvaluationQuantity === 'function') {
        return Pricing.getEvaluationQuantity(this, nextQuantity);
      }

      const fallbackInput = this.form.querySelector('[name="quantity"]');
      const currentInputValue = this.quantityInput || fallbackInput;
      const selectedQuantity = Number.isFinite(nextQuantity)
        ? nextQuantity
        : parseNumberValue(currentInputValue?.value, 1);
      const productCartQuantity = parseNumberValue(this.dataset.productCartQuantity, 0);

      if (this.dataset.showQuantitySelector === 'true') {
        return selectedQuantity;
      }

      if (this.dataset.volumeBlockId || this.closest('.product-info')) {
        return productCartQuantity + selectedQuantity;
      }

      return productCartQuantity;
    }

    getPriceRoot() {
      return this.closest('.product-info')?.querySelector('.product-info__price > .price')
        || this.closest('quick-add-drawer')?.querySelector('.product-info__price > .price')
        || this.closest('product-card')?.querySelector('.price')
        || this.closest('.card')?.querySelector('.price')
        || null;
    }

    resolveVolumeBlock() {
      if (!this.dataset.productId) return null;

      const productId = this.dataset.productId;
      const productInfo = this.closest('.product-info');
      const blockFromId = this.dataset.volumeBlockId
        ? document.getElementById(this.dataset.volumeBlockId)
        : null;
      const blockFromProductInfo = productInfo
        ? productInfo.querySelector(
          productId
            ? `[data-volume-discount][data-product-id="${productId}"]`
            : '[data-volume-discount]'
        )
        : null;

      this.volumeBlock = blockFromId || blockFromProductInfo || null;
      return this.volumeBlock;
    }

    getVolumeTriggerRoot() {
      if (!this.dataset.volumeBlockId) return null;
      return this.closest('.product-info') || this.resolveVolumeBlock();
    }

    bindVolumeTriggerRoot() {
      if (!this.boundHandleVolumeTrigger) return;

      const nextRoot = this.getVolumeTriggerRoot();
      if (this.volumeTriggerRoot === nextRoot) return;

      this.unbindVolumeTriggerRoot();
      this.volumeTriggerRoot = nextRoot;
      if (this.volumeTriggerRoot) {
        this.volumeTriggerRoot.addEventListener('click', this.boundHandleVolumeTrigger);
      }
    }

    unbindVolumeTriggerRoot() {
      if (this.volumeTriggerRoot && this.boundHandleVolumeTrigger) {
        this.volumeTriggerRoot.removeEventListener('click', this.boundHandleVolumeTrigger);
      }
      this.volumeTriggerRoot = null;
    }

    getPricingState(nextQuantity) {
      const Pricing = window.ContarStorefrontPricing;
      if (!Pricing) return null;
      if (!parseNumberValue(this.dataset.productId, 0)) return null;

      const priceRoot = this.getPriceRoot();
      const variantId = this.getVariantId();
      if (!priceRoot || !variantId) return null;

      const compareAtPrice = parseNumberValue(priceRoot.dataset.compareAtPrice, 0);
      const state = Pricing.getState({
        basePrice: parseNumberValue(priceRoot.dataset.basePrice, 0),
        compareAtPrice,
        membershipPresent: this.dataset.membershipPresent || priceRoot.dataset.membershipPresent,
        memberDiscountPercent: this.dataset.memberDiscountPercent || priceRoot.dataset.memberDiscountPercent,
        memberDiscountActive: this.dataset.memberDiscountActive || priceRoot.dataset.memberDiscountActive,
        volumeEnabled: this.dataset.volumeEnabled || priceRoot.dataset.volumeEnabled,
        volumeQtyBreak: this.dataset.volumeQtyBreak || priceRoot.dataset.volumeQtyBreak,
        volumeDiscountPercent: this.dataset.volumeDiscountPercent || priceRoot.dataset.volumeDiscountPercent,
        evaluationQuantity: this.getPricingEvaluationQuantity(nextQuantity),
        cartProductQuantity: this.dataset.productCartQuantity,
        quantityBox: priceRoot.dataset.quantityBox
      });

      return { state, priceRoot };
    }

    syncStorefrontPricing(nextQuantity) {
      const pricingData = this.getPricingState(nextQuantity);
      if (!pricingData) return;

      const { state, priceRoot } = pricingData;
      const Pricing = window.ContarStorefrontPricing;
      if (!Pricing) return;

      Pricing.syncPrice(
        priceRoot,
        state,
        (cents) => ProductForm.formatMoney(cents),
        { available: !this.submitBtn || !this.submitBtn.disabled }
      );

      const volumeBlock = this.resolveVolumeBlock();
      if (volumeBlock) {
        Pricing.syncVolumeBlock(volumeBlock, state, (cents) => ProductForm.formatMoney(cents));
      }

      const sectionRoot = this.closest('.shopify-section');
      if (sectionRoot) {
        sectionRoot.querySelectorAll(
          `[data-storefront-price-mirror] .price[data-product-id="${this.dataset.productId}"]`
        ).forEach((mirrorPriceRoot) => {
          const mirrorState = Pricing.getStateFromPriceRoot(mirrorPriceRoot, {
            basePrice: state.basePrice,
            compareAtPrice: state.compareAtPrice,
            membershipPresent: state.membershipPresent,
            memberDiscountPercent: state.memberDiscountPercent,
            memberDiscountActive: state.memberDiscountActive,
            volumeEnabled: state.volumeEnabled,
            volumeQtyBreak: state.volumeQtyBreak,
            volumeDiscountPercent: state.volumeDiscountPercent,
            evaluationQuantity: state.evaluationQuantity,
            cartProductQuantity: state.cartProductQuantity
          });
          if (mirrorState) {
            Pricing.syncPrice(
              mirrorPriceRoot,
              mirrorState,
              (cents) => ProductForm.formatMoney(cents),
              { available: !this.submitBtn || !this.submitBtn.disabled }
            );
          }
        });
      }

      priceRoot.dataset.evaluationQuantity = `${state.evaluationQuantity}`;
      priceRoot.dataset.finalPrice = `${state.finalPrice}`;
    }

    getHandlingTimePropertiesForQuantity(quantity) {
      if (!this.hasManagedPreorderInventory()) return null;

      const properties = {
        ...parseJsonValue(this.dataset.lineItemProperties, {})
      };
      const state = computePreorderState({
        hasPreorderTag: parseBooleanValue(this.dataset.hasPreorderTag),
        inventoryValid: parseBooleanValue(this.dataset.preorderInventoryValid),
        doralAvailable: parseNumberValue(this.dataset.doralAvailable, 0),
        contarmarketAvailable: parseNumberValue(this.dataset.contarmarketAvailable, 0),
        requestedQuantity: quantity,
        legacyPreorder: parseBooleanValue(this.dataset.preorderLegacy)
      });

      properties[HANDLING_TIME_PROPERTY] = state.handlingTime || HANDLING_TIME_IN_STOCK;
      return properties;
    }

    clearCartMetadata() {
      this.dataset.lineItemKey = '';
      this.dataset.lineItemProperties = '{}';
      this.dataset.cartQuantity = '0';
    }

    getVariantId() {
      return Number(this.dataset.variantId || this.form.querySelector('[name="id"]')?.value || 0);
    }

    async fetchCurrentCart() {
      const response = await fetch(`${theme.routes.cart}.js`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(response.status);
      return response.json();
    }

    dispatchLineItemChange(oldQuantity, newQuantity, cart = null) {
      const variantId = this.getVariantId();
      if (!variantId) return;
      const productId = parseNumberValue(this.dataset.productId, 0);

      this.dispatchEvent(new CustomEvent('on:line-item:change', {
        bubbles: true,
        detail: {
          cart,
          productId,
          variantId,
          oldQuantity,
          newQuantity,
          sourceProductForm: this
        }
      }));
    }

    syncPreorderState() {
      if (!this.handlingTimeInput && !this.preorderMessage) {
        this.syncStorefrontPricing();
        return;
      }

      const state = computePreorderState({
        hasPreorderTag: parseBooleanValue(this.dataset.hasPreorderTag),
        inventoryValid: parseBooleanValue(this.dataset.preorderInventoryValid),
        doralAvailable: parseNumberValue(this.dataset.doralAvailable, 0),
        contarmarketAvailable: parseNumberValue(this.dataset.contarmarketAvailable, 0),
        requestedQuantity: this.getRequestedQuantity(),
        legacyPreorder: parseBooleanValue(this.dataset.preorderLegacy)
      });

      if (this.preorderMessage) {
        this.preorderMessage.hidden = !state.showMessage;
      }

      if (this.handlingTimeInput) {
        if (state.handlingTime) {
          this.handlingTimeInput.disabled = false;
          this.handlingTimeInput.value = state.handlingTime;
        } else {
          this.handlingTimeInput.disabled = true;
          this.handlingTimeInput.value = '';
        }
      }

      this.syncStorefrontPricing();
    }

    syncCartMetadata(cart, options = {}) {
      if (!cart || !Array.isArray(cart.items)) return;

      // Ignore external coordinator-driven syncs (the only caller passing
      // syncQuantityState) while this form has its own optimistic mutation in
      // flight. Another product's cart event can drag this form into the
      // pricing coordinator's affected set (via its pending delta) and hand it
      // a cart snapshot that predates this form's add/change — reverting the
      // optimistic takeover. This form's own add/change handlers are
      // authoritative and reconcile the state themselves.
      if (
        options.syncQuantityState === true
        && options.forceQuantityState !== true
        && (
          this._submitting
          || this._quantityQueuePromise
          || this._pendingVerify
          || this._quantityDebouncePending
        )
      ) {
        return;
      }

      const variantId = this.getVariantId();
      const productId = parseNumberValue(this.dataset.productId, 0);
      const matchingItems = cart.items.filter((item) => Number(item.variant_id) === variantId);
      const cartItem = matchingItems[0];
      const cartQuantity = matchingItems.reduce(
        (total, item) => total + parseNumberValue(item.quantity, 0),
        0
      );

      if (cartItem) {
        this.dataset.lineItemKey = cartItem.key || '';
        this.dataset.lineItemProperties = JSON.stringify(cartItem.properties || {});
        this.dataset.cartQuantity = `${cartQuantity}`;
      } else {
        this.clearCartMetadata();
      }
      this._confirmedQuantity = cartQuantity;

      if (productId) {
        const productCartQuantity = Number.isFinite(options.productCartQuantityOverride)
          ? parseNumberValue(options.productCartQuantityOverride, 0)
          : cart.items.reduce((total, item) => (
          Number(item.product_id) === productId
            ? total + parseNumberValue(item.quantity, 0)
            : total
        ), 0);
        this.dataset.productCartQuantity = `${productCartQuantity}`;
      }

      this.setMaximumInputQuantity();

      const shouldSyncVisibleQuantity = this.quantityInput
        && this.supportsQuantitySelectorTakeover()
        && options.syncQuantityState === true;

      if (shouldSyncVisibleQuantity) {
        // updateQuantityState keeps the visible selector in sync and then refreshes preorder/pricing.
        this.updateQuantityState(cartQuantity);
        return;
      }

      this.syncPreorderState();
      this.syncStorefrontPricing();
    }

    handleCartAdd(evt) {
      if (!evt.detail || !evt.detail.cart) return;

      const eventProductId = parseNumberValue(
        evt.detail.productId || evt.detail.cart.items.find((item) => Number(item.variant_id) === Number(evt.detail.variantId))?.product_id,
        0
      );
      if (
        `${evt.detail.variantId}` !== `${this.dataset.variantId}`
        && eventProductId !== parseNumberValue(this.dataset.productId, 0)
      ) return;

      this.syncCartMetadata(evt.detail.cart);
    }

    handleVariantChange(evt) {
      if (!evt.detail || evt.detail.form !== this.form) return;

      if (evt.detail.variant) {
        this.dataset.variantId = `${evt.detail.variant.id}`;
        const priceRoot = this.getPriceRoot();
        if (priceRoot) {
          priceRoot.dataset.basePrice = `${evt.detail.variant.price || 0}`;
          priceRoot.dataset.compareAtPrice = `${evt.detail.variant.compare_at_price || 0}`;
        }
      }

      this.syncPreorderState();
      this.syncStorefrontPricing();
    }

    handleVolumeTrigger(evt) {
      if (!this.dataset.volumeBlockId) return;
      const trigger = evt.target.closest('[data-volume-discount-trigger]');
      const triggerBlock = trigger?.closest('[data-volume-discount]');
      const volumeBlock = this.resolveVolumeBlock();
      if (!trigger || !triggerBlock || triggerBlock !== volumeBlock || !this.submitBtn) return;

      evt.preventDefault();
      if (this._submitting) return;
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      const missingQuantity = parseNumberValue(trigger.dataset.missingQuantity, 0);
      if (missingQuantity <= 0) return;

      const quantityField = this.quantityInput || this.form.querySelector('[name="quantity"]');
      if (!quantityField) return;
      const minQuantity = ProductForm.getQuantityMinimum(quantityField);
      this._volumeAddQuantity = Math.max(missingQuantity, minQuantity);

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.submitBtn);
      } else {
        this.submitBtn.click();
      }
    }

    static getQuantityMinimum(quantityInput) {
      const configuredMin = quantityInput?.dataset.minQty
        || quantityInput?.getAttribute('min')
        || 1;
      return Math.max(
        1,
        parseNumberValue(configuredMin, 1)
      );
    }

    resetQuantityInputToMinimum() {
      const quantityField = this.quantityInput || this.form.querySelector('[name="quantity"]');
      if (!quantityField) return;

      const minQuantity = ProductForm.getQuantityMinimum(quantityField);
      quantityField.value = `${minQuantity}`;

      if (this.quantitySelector && quantityField === this.quantityInput) {
        this.quantitySelector.currentQty = `${minQuantity}`;
        const minusBtn = this.quantitySelector.querySelector('.btn--minus');
        const plusBtn = this.quantitySelector.querySelector('.btn--plus');
        ProductForm.setQuantityButtonState(null, this.quantityInput, minusBtn, plusBtn);
      }

      this.syncPreorderState();
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          let inventory = null;
          if (dataEl) {
            const variantId = Number(this.form.querySelector('[name="id"]').value);
            const inventoryJson = JSON.parse(dataEl.textContent);
            inventory = inventoryJson.find((item) => item.id === variantId);

            if (inventory
              && inventory.inventory_management
              && inventory.inventory_management.length !== 0
              && inventory.inventory_quantity > 0
              && inventory.inventory_policy !== 'continue') {
              quantityInput.max = inventory.inventory_quantity;

              const currentQuantity = Number(quantityInput.value);
              if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                quantityInput.value = quantityInput.max;
              }
            }
          }

          const preorderMin = Number(this.dataset.preorderMinimum || 0);
          const isProductMinimumEnabled = this.dataset.enableProductMinimum === 'true';
          const usesCartAwareMinimum = this.dataset.baseProductMinimum !== undefined;
          let baseMin = Math.max(1, parseNumberValue(this.dataset.baseProductMinimum, 1));

          if (isProductMinimumEnabled && preorderMin > 1) {
            const invQty = inventory ? (inventory.inventory_quantity || 0) : Infinity;
            const isManaged = inventory?.inventory_management?.length > 0;
            const minApplies = !isManaged || invQty >= preorderMin;
            baseMin = minApplies ? preorderMin : 1;
          } else {
            baseMin = 1;
          }

          if (usesCartAwareMinimum) {
            this.dataset.baseProductMinimum = `${baseMin}`;
          }

          const productCartQuantity = parseNumberValue(this.dataset.productCartQuantity, 0);
          const effectiveMin = usesCartAwareMinimum && productCartQuantity > 0 ? 1 : baseMin;

          quantityInput.min = effectiveMin;
          quantityInput.dataset.minQty = effectiveMin;
          if (Number(quantityInput.value) < effectiveMin) {
            quantityInput.value = `${effectiveMin}`;
            quantitySelector.currentQty = `${effectiveMin}`;
          }

          const minMsg = productInfo.querySelector('.preorder-minimum');
          if (minMsg) minMsg.hidden = effectiveMin <= 1;

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (!evt.detail) return;

      const variantMatches = `${evt.detail.variantId}` === `${this.dataset.variantId}`;
      const eventProductId = parseNumberValue(
        evt.detail.productId || evt.detail.cart?.items.find((item) => Number(item.variant_id) === Number(evt.detail.variantId))?.product_id,
        0
      );
      if (
        !variantMatches
        && eventProductId !== parseNumberValue(this.dataset.productId, 0)
      ) return;

      // This form already reconciled its own current response before emitting
      // the event. Ignoring the echo prevents a second visible-state write.
      if (evt.detail.sourceProductForm === this) return;

      // Another surface can publish a cart snapshot while this form has a
      // newer optimistic intent. That snapshot must not replace the input.
      if (
        variantMatches
        && (
          this._quantityDebouncePending
          || this._quantityQueuePromise
          || (this._queuedQuantity !== null && this._queuedQuantity !== undefined)
        )
      ) {
        return;
      }

      if (evt.detail.cart) {
        this.syncCartMetadata(evt.detail.cart);
      } else {
        if (eventProductId === parseNumberValue(this.dataset.productId, 0)) {
          const currentProductCartQuantity = parseNumberValue(this.dataset.productCartQuantity, 0);
          const quantityDelta = parseNumberValue(evt.detail.newQuantity, 0)
            - parseNumberValue(evt.detail.oldQuantity, 0);
          this.dataset.productCartQuantity = `${Math.max(0, currentProductCartQuantity + quantityDelta)}`;
        }

        if (variantMatches && parseNumberValue(evt.detail.newQuantity, 0) <= 0) {
          this.clearCartMetadata();
        } else if (variantMatches) {
          this.dataset.cartQuantity = `${parseNumberValue(evt.detail.newQuantity, 0)}`;
        }

        this.setMaximumInputQuantity();
      }

      if (this.quantityInput && variantMatches) {
        this.updateQuantityState(parseNumberValue(evt.detail.newQuantity, 0));
      } else if (this.quantityInput) {
        this.syncPreorderState();
      } else {
        this.syncPreorderState();
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      const newQty = parseInt(evt.target.value, 10) || 0;
      const minQty = parseInt(evt.target.dataset.minQty, 10) || 1;
      const desiredQuantity = newQty > 0 && newQty < minQty ? 0 : newQty;
      const confirmedQuantity = parseNumberValue(this.dataset.cartQuantity, 0);
      const previousDesiredQuantity = Number.isFinite(this._desiredQuantity)
        ? this._desiredQuantity
        : confirmedQuantity;

      this._desiredQuantity = desiredQuantity;
      this._quantityIntentVersion = (this._quantityIntentVersion || 0) + 1;
      this._quantityDebouncePending = true;

      // The selector already changed synchronously. Keep the header count on
      // the same optimistic intent without waiting for the network queue.
      const optimisticDelta = desiredQuantity - previousDesiredQuantity;
      if (optimisticDelta !== 0) ProductForm.optimisticCartIconIncrement(optimisticDelta);
      this.updateQuantityButtonState();

      this.debouncedQuantityCommit({
        quantity: desiredQuantity,
        intentVersion: this._quantityIntentVersion
      });
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      // Prevent double-clicks: use JS flag for optimistic path, aria-disabled for membership
      if (this._submitting) return;
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this._submitting = true;
      // Each user-initiated mutation bumps _syncVersion; the background
      // verification below only applies its snapshot if no newer mutation
      // started in the meantime.
      this._syncVersion = (this._syncVersion || 0) + 1;
      const submitVersion = this._syncVersion;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();
      this.clearControlError(this.submitBtn, this.formError);

      // Invalidate any in-flight stale refreshCartDrawer from a previous updateQuantity
      const cartDrawer = this.getCartDrawer();
      if (cartDrawer) {
        cartDrawer._renderVersion = (cartDrawer._renderVersion || 0) + 1;
      }

      const isMembership = !!this.closest('.btn-membership');
      const volumeAddQuantity = Number.isFinite(this._volumeAddQuantity)
        ? this._volumeAddQuantity
        : null;
      const isVolumeAdd = volumeAddQuantity !== null;
      const formQuantity = parseInt(this.form.querySelector('[name="quantity"]')?.value, 10) || 1;
      const addedQty = volumeAddQuantity || formQuantity;
      const supportsQuantitySelectorTakeover = this.supportsQuantitySelectorTakeover();
      const currentCartQuantity = parseNumberValue(this.dataset.cartQuantity, 0);
      const pricingCoordinator = window.ContarStorefrontPricing?.coordinator || null;
      const shouldUpdateExistingLine = !isMembership
        && !supportsQuantitySelectorTakeover
        && currentCartQuantity > 0
        && Boolean(this.dataset.lineItemKey);
      const wasAlreadyInCart = supportsQuantitySelectorTakeover
        && this.dataset.showQuantitySelector === 'true';
      let appliedOptimisticPricingDelta = false;

      if (!isMembership) {
        // Optimistic UI: instant success + immediate switch to qty-input
        if (!shouldUpdateExistingLine) {
          ProductForm.optimisticCartIconIncrement(addedQty);
          if (pricingCoordinator) {
            pricingCoordinator.registerPendingDelta(parseNumberValue(this.dataset.productId, 0), addedQty);
            appliedOptimisticPricingDelta = true;
          }
        }
        this.submitBtn.classList.add('is-success');

        if (this.quantitySelector && supportsQuantitySelectorTakeover) {
          const optimisticQty = wasAlreadyInCart
            ? (parseInt(this.quantityInput.value, 10) || 0) + addedQty
            : addedQty;
          this.setAttribute('data-show-quantity-selector', 'true');
          this.syncQuantityChangeBinding();
          this.quantityInput.value = optimisticQty;
          this.quantityInput.dataset.initialValue = optimisticQty;
          this.quantitySelector.currentQty = optimisticQty;
          this._confirmedQuantity = currentCartQuantity;
          this._desiredQuantity = optimisticQty;

          // Update +/- button states (e.g. disable + at max inventory)
          const minusBtn = this.quantitySelector.querySelector('.btn--minus');
          const plusBtn = this.quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, this.quantityInput, minusBtn, plusBtn);
        }
      } else {
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('is-loading');
      }

      this.syncPreorderState();
      const formData = new FormData(this.form);
      if (isVolumeAdd) {
        formData.set('quantity', `${addedQty}`);
      }
      let sections = 'cart-icon-bubble';
      const cartDrawerSectionId = this.getCartDrawerSectionId();
      if (cartDrawerSectionId) sections += `,${cartDrawerSectionId}`;

      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          quickOrderSections.push({
            id: `quick-order-list_${sectionId}`,
            section: sectionId,
            selector: `#quick-order-list_${sectionId}`
          });
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        if (shouldUpdateExistingLine) {
          await this.updateQuantity(currentCartQuantity + addedQty, {
            previousQuantity: currentCartQuantity,
            clearSubmitSuccessOnError: true,
            resetSelectorOnSuccess: Boolean(this.closest('.product-info'))
          });
          return;
        }

        this._pendingAdd = fetch(theme.routes.cartAdd, fetchRequestOpts);
        const response = await this._pendingAdd;
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          ProductForm.updateCartIcon(data);

          const liveRegion = document.getElementById('atc-live-region');
          if (liveRegion) {
            const qty = data.quantity || 1;
            liveRegion.textContent = `${qty} ${data.product_title} added to cart`;
            setTimeout(() => { liveRegion.textContent = ''; }, 5000);
          }

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          const currentCartDrawer = this.getCartDrawer();
          if (currentCartDrawer) {
            currentCartDrawer.renderContents(
              data,
              !quickAddDrawer && ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
                || theme.settings.afterAtc === 'drawer')
            );

            if (quickOrderSections.length) {
              quickOrderSections.forEach((quickOrderSection) => {
                const sectionEl = document.getElementById(quickOrderSection.id);
                if (!sectionEl) return;

                const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                el.innerHTML = ProductForm.getElementHTML(
                  data.sections[quickOrderSection.section],
                  quickOrderSection.selector
                );
              });
            }
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refreshCartItems();
              }
            }
          }
        }

        // Background: verify actual cart state and dispatch events
        this._pendingVerify = (async () => {
          try {
            const newCartResponse = await fetch(`${theme.routes.cart}.js`);
            if (!newCartResponse.ok) return;
            const newCartData = await newCartResponse.json();
            const newCartItem = newCartData.items.find(
              (item) => item.variant_id === data.variant_id
            );

            const newQuantity = newCartItem ? newCartItem.quantity : (data.quantity || 1);
            if (appliedOptimisticPricingDelta && pricingCoordinator) {
              pricingCoordinator.releasePendingDelta(
                parseNumberValue(this.dataset.productId, 0),
                addedQty,
                { sync: false }
              );
              appliedOptimisticPricingDelta = false;
            }

            // A newer mutation (stepper change or another submit) started after
            // this add: its own confirmation supersedes this cart snapshot.
            // Writing stale state here is the source of the "qty jumps back" bug.
            if (this._syncVersion !== submitVersion) return;

            if (wasAlreadyInCart) {
              this.dispatchEvent(new CustomEvent('on:line-item:change', {
                bubbles: true,
                detail: {
                  cart: newCartData,
                  productId: parseNumberValue(this.dataset.productId, 0),
                  variantId: data.variant_id,
                  newQuantity
                }
              }));
            } else {
              this.dispatchEvent(new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: {
                  cart: newCartData,
                  productId: parseNumberValue(this.dataset.productId, 0),
                  variantId: data.variant_id
                }
              }));
            }

            // If product NOT in cart (add silently failed), revert optimistic UI
            if (!newCartItem && !isMembership) {
              this.submitBtn.classList.remove('is-success');
              if (this.quantitySelector && supportsQuantitySelectorTakeover && !wasAlreadyInCart) {
                this.setAttribute('data-show-quantity-selector', 'false');
                this.syncQuantityChangeBinding();
              }
              // Refresh drawer to sync UI with actual cart state
              const currentCartDrawer = this.getCartDrawer();
              if (currentCartDrawer) currentCartDrawer.refreshCartDrawer();
              return;
            }

            // Update metadata only — never overwrite user's displayed qty
            this.syncCartMetadata(newCartData);
            // PDP ("quantity to add"): after a successful add the selector goes back to its minimum,
            // so it never reads as the quantity already in the cart.
            if (!supportsQuantitySelectorTakeover && !isMembership && this.closest('.product-info')) {
              this.resetQuantityInputToMinimum();
              this.syncStorefrontPricing();
            }
            if (this.quantitySelector && supportsQuantitySelectorTakeover) {
              this.quantityInput.dataset.initialValue = newQuantity;
              this.setAttribute('data-show-quantity-selector', 'true');
              this.syncQuantityChangeBinding();
              // Update +/- button states
              const minusBtn = this.quantitySelector.querySelector('.btn--minus');
              const plusBtn = this.quantitySelector.querySelector('.btn--plus');
              ProductForm.setQuantityButtonState(null, this.quantityInput, minusBtn, plusBtn);
            }
          } catch (e) {
            // Silent fail for background verification
          } finally {
            this._pendingVerify = null;
          }
        })();
      } catch (error) {
        if (!isMembership) {
          ProductForm.optimisticCartIconIncrement(-addedQty);
          if (appliedOptimisticPricingDelta && pricingCoordinator) {
            pricingCoordinator.releasePendingDelta(parseNumberValue(this.dataset.productId, 0), addedQty);
            appliedOptimisticPricingDelta = false;
          }
          // Revert optimistic UI: show ATC button again
          this.submitBtn.classList.remove('is-success');
          if (this.quantitySelector && supportsQuantitySelectorTakeover && !wasAlreadyInCart) {
            this.setAttribute('data-show-quantity-selector', 'false');
            this.syncQuantityChangeBinding();
          }
        }

        if (!this.errorMsg || this.errorMsg.hidden) {
          this.setErrorMsgState(/^[0-9]+$/.test(error.message) ? theme.strings.cartError : error.message);
        }

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg ? this.errorMsg.textContent : ''
          }
        }));

        if (this.errorMsg) {
          this.setControlError(this.submitBtn, this.errorMsg);
          this.submitBtn.focus();
        }

        const currentCartDrawer = this.getCartDrawer();
        if (currentCartDrawer) currentCartDrawer.refreshCartDrawer();
      } finally {
        this._volumeAddQuantity = null;
        this._pendingAdd = null;
        this._submitting = false;
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');

        if (isMembership) {
          this.submitBtn.classList.remove('is-success');
          this.submitBtn.classList.add('is-success');
          setTimeout(() => { this.submitBtn.classList.remove('is-success'); }, 450);
        } else if (!this.quantitySelector || this.dataset.showQuantitySelector !== 'true') {
          // On PDP (no qty-input takeover), clear check after brief display
          setTimeout(() => { this.submitBtn.classList.remove('is-success'); }, 600);
        }
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Optimistically increments/decrements the cart icon count number.
     * @param {number} delta - Amount to add (negative to subtract).
     */
    static optimisticCartIconIncrement(delta) {
      const countEl = document.querySelector('.header__cart-count-number');
      if (!countEl) return;
      const current = parseInt(countEl.textContent, 10) || 0;
      const newCount = Math.max(0, current + delta);
      countEl.textContent = newCount;
    }

    updateQuantityButtonState() {
      if (!this.quantitySelector || !this.quantityInput) return;

      const minusBtn = this.quantitySelector.querySelector('.btn--minus');
      const plusBtn = this.quantitySelector.querySelector('.btn--plus');
      if (!minusBtn || !plusBtn) return;

      ProductForm.setQuantityButtonState(null, this.quantityInput, minusBtn, plusBtn);
    }

    updateQuantityState(newQuantity) {
      const minQty = parseInt(this.quantityInput.dataset.minQty, 10) || 1;
      const supportsQuantitySelectorTakeover = this.supportsQuantitySelectorTakeover();

      this._confirmedQuantity = newQuantity;
      this._desiredQuantity = newQuantity;
      this._quantityDebouncePending = false;
      
      if (!supportsQuantitySelectorTakeover) {
        if (newQuantity <= 0) {
          this.quantityInput.value = minQty;
          this.clearCartMetadata();
          this.submitBtn.classList.remove('is-success');
        } else {
          this.dataset.cartQuantity = `${newQuantity}`;
        }

        this.updateQuantityButtonState();
        this.syncPreorderState();
        return;
      }

      if (newQuantity <= 0) {
        // Producto eliminado del carrito: resetear al mínimo y ocultar selector
        this.quantityInput.value = minQty;
        this.quantityInput.dataset.initialValue = minQty;
        this.quantitySelector.currentQty = minQty;
        this.setAttribute('data-show-quantity-selector', 'false');
        this.syncQuantityChangeBinding();
        this.submitBtn.classList.remove('is-success');
        this.clearCartMetadata();
      } else {
        this.quantityInput.value = newQuantity;
        this.quantityInput.dataset.initialValue = newQuantity;
        this.quantitySelector.currentQty = newQuantity;
        this.setAttribute('data-show-quantity-selector', 'true');
        this.syncQuantityChangeBinding();
        this.dataset.cartQuantity = `${newQuantity}`;
      }
      this.updateQuantityButtonState();
      this.syncPreorderState();
    }

    /**
     * Public entry point for quantity mutations. Queues the update so only
     * one /cart/change request is in flight per product-form; rapid changes
     * coalesce to the latest requested value (the queue always converges to
     * the user's most recent intent).
     * @param {number} quantity - Absolute quantity desired.
     * @param {object} [options] - Options forwarded to performQuantityUpdate.
     * @returns {Promise} Resolves when the queue drains.
     */
    updateQuantity(quantity, options = {}) {
      this._queuedQuantity = quantity;
      this._queuedOptions = options;

      if (this._quantityQueuePromise) return this._quantityQueuePromise;

      if (this.quantitySelector) this.quantitySelector.setAttribute('aria-busy', 'true');

      this._quantityQueuePromise = (async () => {
        try {
          while (this._queuedQuantity !== null && this._queuedQuantity !== undefined) {
            const nextQuantity = this._queuedQuantity;
            const nextOptions = this._queuedOptions || {};
            this._queuedQuantity = null;
            this._queuedOptions = null;

            const confirmedQuantity = parseNumberValue(this.dataset.cartQuantity, 0);
            if (nextQuantity === confirmedQuantity && !Number.isFinite(nextOptions.previousQuantity)) {
              // No-op: the input already matches the confirmed cart state.
              if (!this.isQuantityIntentSuperseded(nextQuantity, nextOptions)) {
                this.updateQuantityState(confirmedQuantity);
              }
              continue;
            }

            // eslint-disable-next-line no-await-in-loop
            await this.performQuantityUpdate(nextQuantity, nextOptions);
          }
        } finally {
          this._quantityQueuePromise = null;
          if (this.quantitySelector) this.quantitySelector.removeAttribute('aria-busy');
        }
      })();

      return this._quantityQueuePromise;
    }

    isQuantityIntentSuperseded(quantity, options = {}) {
      if (!Number.isFinite(options.intentVersion)) return false;

      return options.intentVersion !== this._quantityIntentVersion
        || (Number.isFinite(this._desiredQuantity) && this._desiredQuantity !== quantity);
    }

    async performQuantityUpdate(quantity, options = {}) {
      // Wait for any in-flight /cart/add to complete before sending /cart/change
      if (this._pendingAdd) {
        try { await this._pendingAdd; } catch (e) { /* handled in handleSubmit */ }
      }

      // Supersede any pending background add-verification snapshot.
      this._syncVersion = (this._syncVersion || 0) + 1;

      // Invalidate any in-flight stale drawer refresh (same pattern as handleSubmit)
      const cartDrawer = this.getCartDrawer();
      if (cartDrawer) {
        cartDrawer._renderVersion = (cartDrawer._renderVersion || 0) + 1;
      }

      const lineErrors = this.quantityError || this.querySelector('.js-product-form-errors');

      // Optimistic cart icon: update immediately.
      // Single source of truth: dataset.cartQuantity holds the last
      // server-confirmed quantity. The mutation queue guarantees it is
      // current at send time (no interleaved mutations).
      const oldQuantity = Number.isFinite(options.previousQuantity)
        ? options.previousQuantity
        : parseNumberValue(this.dataset.cartQuantity, 0);
      const delta = quantity - oldQuantity;
      const pricingCoordinator = window.ContarStorefrontPricing?.coordinator || null;
      const productId = parseNumberValue(this.dataset.productId, 0);
      let appliedOptimisticPricingDelta = false;
      if (
        delta !== 0
        && !options.skipOptimisticIncrement
        && !options.optimisticIncrementApplied
      ) {
        ProductForm.optimisticCartIconIncrement(delta);
      }
      if (delta !== 0 && pricingCoordinator && !options.skipOptimisticIncrement) {
        pricingCoordinator.registerPendingDelta(productId, delta);
        appliedOptimisticPricingDelta = true;
      }

      // Build sections list for inline rendering (skip second fetch)
      const sections = ['cart-icon-bubble', 'free-shipping-notice'];
      const cartDrawerSectionId = this.getCartDrawerSectionId();
      if (cartDrawerSectionId) sections.push(cartDrawerSectionId);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      const payload = {
        id: this.dataset.lineItemKey || this.dataset.variantId,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      };
      const usedLineItemKey = Boolean(this.dataset.lineItemKey);
      const handlingTimeProperties = this.getHandlingTimePropertiesForQuantity(quantity);
      if (handlingTimeProperties) {
        payload.properties = handlingTimeProperties;
      }

      fetchRequestOpts.body = JSON.stringify(payload);

      let updateSucceeded = false;
      let responseWasCurrent = false;
      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);
        updateSucceeded = true;
        this.syncCartMetadata(data);
        const confirmedQuantity = parseNumberValue(this.dataset.cartQuantity, quantity);

        if (appliedOptimisticPricingDelta && pricingCoordinator) {
          pricingCoordinator.releasePendingDelta(productId, delta, { sync: false });
          appliedOptimisticPricingDelta = false;
        }

        // The response is still useful for confirmed metadata and the latest
        // line key, but an older intent must never render over a newer one.
        if (this.isQuantityIntentSuperseded(quantity, options)) return;

        responseWasCurrent = true;
        const desiredQuantity = Number.isFinite(this._desiredQuantity)
          ? this._desiredQuantity
          : quantity;
        if (confirmedQuantity !== desiredQuantity && options.optimisticIncrementApplied) {
          ProductForm.optimisticCartIconIncrement(confirmedQuantity - desiredQuantity);
        }

        this.updateQuantityState(confirmedQuantity);
        // PDP add with the product already in the cart: same reset as the /cart/add path.
        if (options.resetSelectorOnSuccess) {
          this.resetQuantityInputToMinimum();
          this.syncStorefrontPricing();
        }

        // Render sections inline only for the final, current intent.
        if (data.sections) {
          this.renderCartSections(data.sections);
        }

        this.dispatchLineItemChange(oldQuantity, confirmedQuantity, data);

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
          this.clearControlError(this.quantityInput, lineErrors);
        }
      } catch (error) {
        if (this.isQuantityIntentSuperseded(quantity, options)) {
          if (appliedOptimisticPricingDelta && pricingCoordinator) {
            pricingCoordinator.releasePendingDelta(productId, delta, { sync: false });
            appliedOptimisticPricingDelta = false;
          }

          try {
            const refreshedCart = await this.fetchCurrentCart();
            this.syncCartMetadata(refreshedCart);
          } catch (refreshError) {
            // The latest queued intent will retry against the best metadata available.
          }
          return;
        }

        if (usedLineItemKey && !options.hasRetriedKeyRefresh) {
          try {
            const refreshedCart = await this.fetchCurrentCart();
            this.syncCartMetadata(refreshedCart);

            if (this.dataset.lineItemKey && this.dataset.lineItemKey !== payload.id) {
              if (appliedOptimisticPricingDelta && pricingCoordinator) {
                pricingCoordinator.releasePendingDelta(productId, delta, { sync: false });
                appliedOptimisticPricingDelta = false;
              }
              // Retry directly (not via the queue): re-queueing would lose the
              // retry context and could deadlock the draining loop.
              return this.performQuantityUpdate(quantity, {
                ...options,
                hasRetriedKeyRefresh: true,
                skipOptimisticIncrement: true,
                revertOptimisticIncrementOnError: true,
                previousQuantity: oldQuantity
              });
            }
          } catch (refreshError) {
            // Fall through to the regular error path below.
          }
        }

        if (options.clearSubmitSuccessOnError) {
          this.submitBtn.classList.remove('is-success');
        }

        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
          this.setControlError(this.quantityInput, lineErrors);
          this.quantityInput.focus();
        }

        // Revert optimistic icon
        if (delta !== 0 && (
          options.optimisticIncrementApplied
          || !options.skipOptimisticIncrement
          || options.revertOptimisticIncrementOnError
        )) {
          ProductForm.optimisticCartIconIncrement(-delta);
        }
        if (appliedOptimisticPricingDelta && pricingCoordinator) {
          pricingCoordinator.releasePendingDelta(productId, delta);
          appliedOptimisticPricingDelta = false;
        }

        const reconciledCart = window.ContarCartReconciler?.rehydrate
          ? await window.ContarCartReconciler.rehydrate('quantity-error')
          : null;
        let authoritativeCart = reconciledCart;

        if (!authoritativeCart) {
          try {
            authoritativeCart = await this.fetchCurrentCart();
          } catch (refreshError) {
            // The local confirmed quantity remains the final fallback below.
          }
        }

        if (authoritativeCart) {
          // The inventory error can still leave Shopify's cart at the maximum
          // available quantity. Apply that authoritative snapshot to this form
          // even though its mutation queue has not finished unwinding yet.
          this.syncCartMetadata(authoritativeCart, {
            syncQuantityState: true,
            forceQuantityState: true
          });
          if (document.activeElement === this.quantityInput) this.quantityInput.blur();
        } else {
          const confirmedQuantity = parseNumberValue(this.dataset.cartQuantity, oldQuantity);
          // Skip the UI reset if a newer update is queued: it will settle the state.
          if (this._queuedQuantity === null || this._queuedQuantity === undefined) {
            this.updateQuantityState(confirmedQuantity);
          }
        }

        if (!reconciledCart) {
          const currentCartDrawer = this.getCartDrawer();
          if (currentCartDrawer) {
            currentCartDrawer.refreshCartDrawer(true);
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refreshCartItems();
              }
            }
          }
        }
      } finally {
        // Skip the state sync if a newer update is queued: syncing here would
        // overwrite the user's latest input value with an intermediate one.
        if (
          updateSucceeded
          && responseWasCurrent
          && (this._queuedQuantity === null || this._queuedQuantity === undefined)
        ) {
          this.updateQuantityState(parseNumberValue(this.dataset.cartQuantity, quantity));
        }
      }
    }

    /**
     * Renders cart sections from inline response data (avoids a second fetch).
     * @param {object} sections - Section HTML keyed by section id.
     */
    renderCartSections(sections) {
      // Update cart-icon-bubble
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = sections['cart-icon-bubble'];
      }

      // Update cart drawer count from icon bubble
      const drawerCount = document.getElementById('cart-drawer__count');
      if (drawerCount && sections['cart-icon-bubble']) {
        const tmp = document.createElement('div');
        tmp.innerHTML = sections['cart-icon-bubble'];
        const countEl = tmp.querySelector('.header__cart-count-number');
        if (countEl) drawerCount.innerHTML = `(${countEl.textContent})`;
      }

      // Update free-shipping-notice
      const freeShipping = document.getElementById('free-shipping-notice');
      if (freeShipping && sections['free-shipping-notice']) {
        const tmp = document.createElement('div');
        tmp.innerHTML = sections['free-shipping-notice'];
        const notice = tmp.querySelector('.free-shipping-notice');
        if (notice) freeShipping.innerHTML = notice.outerHTML;
      }

      // Update cart drawer sections (cart-items + summary)
      const cartDrawerSectionId = this.getCartDrawerSectionId();
      if (cartDrawerSectionId) {
        const sectionHtml = sections[cartDrawerSectionId];
        if (sectionHtml) {
          const cartItemsEl = document.getElementById('cart-items');
          if (cartItemsEl) {
            const html = ProductForm.getElementHTML(sectionHtml, 'cart-items');
            if (html) cartItemsEl.innerHTML = html;
          }

          const cartDrawerSummary = document.querySelector('.cart-drawer__summary');
          if (cartDrawerSummary) {
            const renderedSummary = ProductForm.getElement(sectionHtml, '.cart-drawer__summary');
            if (renderedSummary) {
              cartDrawerSummary.innerHTML = renderedSummary.innerHTML;
              ProductForm.copyStorefrontMembershipContext(renderedSummary, cartDrawerSummary);
            }
          }

          window.initLazyImages();
        }
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      return ProductForm.getElement(html, selector)?.innerHTML || '';
    }

    static getElement(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      return tmpl.content.querySelector(selector);
    }

    static copyStorefrontMembershipContext(sourceEl, targetEl) {
      if (!sourceEl || !targetEl || !sourceEl.hasAttribute('data-storefront-membership-context')) return;

      const contexts = new Set([
        targetEl,
        ...document.querySelectorAll('[data-storefront-membership-context]')
      ]);
      contexts.forEach((contextEl) => {
        contextEl.setAttribute('data-storefront-membership-context', '');
        ['data-storefront-membership-present', 'data-storefront-membership-source'].forEach((attribute) => {
          if (sourceEl.hasAttribute(attribute)) {
            contextEl.setAttribute(attribute, sourceEl.getAttribute(attribute));
          } else {
            contextEl.removeAttribute(attribute);
          }
        });
      });
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }
  const getMembershipUiElements = () => Array.from(document.querySelectorAll('[data-membership-ui="true"]'));

  const parseMembershipHandles = (handles) => handles
    .split(',')
    .map((handle) => handle.trim())
    .filter((handle) => handle.length > 0);

  const updateMembershipUI = (cart) => {
    const elements = getMembershipUiElements();
    if (!elements.length || !cart || !Array.isArray(cart.items)) return;

    elements.forEach((element) => {
      const handles = parseMembershipHandles(element.dataset.membershipHandles || '');
      if (!handles.length) return;
      const hasMembership = cart.items.some((item) => handles.includes(item.handle));
      if (hasMembership) {
        element.setAttribute('hidden', '');
      } else {
        element.removeAttribute('hidden');
      }
    });
  };

  const refreshMembershipUI = async () => {
    try {
      const response = await fetch(`${theme.routes.cart}.js`);
      if (!response.ok) return;
      const cartData = await response.json();
      updateMembershipUI(cartData);
    } catch (error) {
    }
  };

  const refreshCartIconBubble = async () => {
    try {
      const response = await fetch(`?sections=cart-icon-bubble`);
      if (!response.ok) return;
      const sections = await response.json();
      ProductForm.updateCartIcon({ sections });
    } catch (error) {
    }
  };

  if (!window.__membershipUiListener) {
    window.__membershipUiListener = true;
    const handleMembershipCartEvent = (event) => {
      if (event.detail && event.detail.cart) {
        updateMembershipUI(event.detail.cart);
      } else {
        refreshMembershipUI();
      }
    };
    document.addEventListener('on:cart:add', handleMembershipCartEvent);
    document.addEventListener('on:line-item:change', handleMembershipCartEvent);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', refreshMembershipUI, { once: true });
    } else {
      refreshMembershipUI();
    }
  }
  // Membership add/remove button toggle
(function initMembershipButton() {
  function setup() {
    const addToCartBtn = document.querySelector('.btn-membership .btn[name="add"]');
    const productContainer = document.querySelector('.btn-membership');

    if (!addToCartBtn || !productContainer) return;

    let isProductInCart = false;

    addToCartBtn.addEventListener('click', async function(event) {
      event.preventDefault();

      if (isProductInCart) {
        await removeFromCart();
      } else {
        try {
          await addToCart();
        } catch (error) {
          // Error adding product to cart
        }
      }
    });

    async function addToCart() {
      const productForm = productContainer.closest('product-form');
      if (!productForm) return;

      const formEl = productForm.querySelector('.js-product-form') || productForm.form;
      if (!formEl) return;

      const variantId = formEl.querySelector('[name="id"]')?.value;
      if (!variantId) return;

      return new Promise((resolve, reject) => {
        const cleanup = () => {
          productForm.removeEventListener('on:cart:add', onSuccess);
          productForm.removeEventListener('on:line-item:change', onSuccess);
          productForm.removeEventListener('on:cart:error', onError);
        };

        const onSuccess = (evt) => {
          if (!evt.detail || `${evt.detail.variantId}` !== `${variantId}`) return;
          cleanup();
          isProductInCart = true;
          toggleButtonState(true);
          resolve();
        };

        const onError = () => {
          cleanup();
          reject();
        };

        productForm.addEventListener('on:cart:add', onSuccess);
        productForm.addEventListener('on:line-item:change', onSuccess);
        productForm.addEventListener('on:cart:error', onError);

        if (formEl.requestSubmit) {
          formEl.requestSubmit();
        } else {
          formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }

    async function removeFromCart() {
      const productForm = productContainer.closest('product-form');
      const variantId = productForm.querySelector('[name="id"]').value;

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 0,
        }),
      };

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        isProductInCart = false;
        toggleButtonState(false);
        updateMembershipUI(data);
        refreshCartIconBubble();
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      } catch (error) {
      }
    }

    function toggleButtonState(isInCart) {
      if (isInCart) {
        addToCartBtn.innerHTML = "Remove from Cart";
      } else {
        addToCartBtn.innerHTML = "Add to Cart";
      }
    }

    async function syncStateFromCart() {
      const productForm = productContainer.closest('product-form');
      if (!productForm) return;

      const formEl = productForm.querySelector('.js-product-form') || productForm.form;
      if (!formEl) return;

      const variantId = formEl.querySelector('[name="id"]')?.value;
      if (!variantId) return;

      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return;
        const cartData = await response.json();
        isProductInCart = cartData.items
          .some((item) => `${item.variant_id}` === `${variantId}`);
        toggleButtonState(isProductInCart);
        updateMembershipUI(cartData);
      } catch (error) {
        // Ignore cart state errors
      }
    }

    syncStateFromCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup, { once: true });
  } else {
    setup();
  }
})();





  customElements.define('product-form', ProductForm);
}
